// PATH: src/middleware/AuthGuard.jsx
import { useEffect } from "react";
import { Navigate, useLocation, useNavigate, useOutlet } from "react-router";

import { useAuthContext } from "app/contexts/auth/context";
import { useAuthModalContext } from "app/contexts/authModal/context";
import { SplashScreen } from "components/template/SplashScreen";
import { hasValidSession, isManualLogoutRecent } from "utils/session";

// ---------------------------------------------------------------------------
// Access is granted ONLY when BOTH are true:
//   1. context says isAuthenticated
//   2. the cookies RIGHT NOW still hold a valid session
//      (access_token valid + not expired + email + userid)
// Before, only (1) was checked - and (1) was computed once at page load, so a
// deleted/expired/half-cleared cookie still let people in until a hard reload.
// ---------------------------------------------------------------------------

export default function AuthGuard() {
  const outlet = useOutlet();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isInitialized, verifySession } = useAuthContext();
  const { openAuthModal } = useAuthModalContext();

  // Live check on every render of the guard (every route change re-renders it)
  const sessionOk = isInitialized && isAuthenticated && hasValidSession();

  // Sync context with cookies on every navigation (logs out if cookies died)
  useEffect(() => {
    if (isInitialized) verifySession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, location.pathname, location.search]);

  useEffect(() => {
    // no login modal right after a manual logout (user is heading to home)
    if (isInitialized && !sessionOk && !isManualLogoutRecent()) {
      const redirectPath = location.pathname + location.search;
      openAuthModal({
        label: "Raajneeti AI",
        onSuccess: () => navigate(redirectPath, { replace: true }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, sessionOk, location.pathname, location.search]);

  // Wait for auth initialization before deciding anything
  if (!isInitialized) {
    return <SplashScreen />;
  }

  // No valid session -> protected content is never rendered
  if (!sessionOk) {
    return <Navigate to="/" replace />;
  }

  return <>{outlet}</>;
}